import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['audioInput', 'status', 'timer', 'composer', 'recordButton', 'liveDot', 'readyDot', 'textInput', 'defaultSubmit']

  connect () {
    this.mediaRecorder = null
    this.stream = null
    this.chunks = []
    this.recordingStartedAt = null
    this.maxDurationMs = 60 * 1000
    this._timer = null
    this._submitAfterStop = false
    this.setIdleState()
  }

  disconnect () {
    this.stopTracks()
    this.stopTimer()
  }

  async toggleRecording (event) {
    event.preventDefault()

    if (this.recording()) {
      this.stopRecording({ submitAfterStop: false })
      return
    }

    await this.startRecording()
  }

  sendRecording (event) {
    event.preventDefault()

    if (this.recording()) {
      this.stopRecording({ submitAfterStop: true })
      return
    }

    if (this.hasAudioInputTarget && this.audioInputTarget.files?.length > 0) {
      this.element.requestSubmit()
    }
  }

  clearAudio (event) {
    event.preventDefault()
    if (!this.hasAudioInputTarget) return

    this.audioInputTarget.value = ''
    this.chunks = []
    this._submitAfterStop = false
    this.setIdleState()
  }

  recording () {
    return this.mediaRecorder && this.mediaRecorder.state === 'recording'
  }

  async startRecording () {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this.setErrorState('Voice note is not supported on this device/browser.')
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = this.supportedMimeType()
      this.mediaRecorder = mimeType ? new MediaRecorder(this.stream, { mimeType }) : new MediaRecorder(this.stream)
      this.chunks = []
      this.recordingStartedAt = Date.now()

      this.mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data?.size) this.chunks.push(event.data)
      })

      this.mediaRecorder.addEventListener('stop', () => {
        this.buildAudioFile()
      }, { once: true })

      this.mediaRecorder.start()
      this.setRecordingState()
      this.updateTimer(0)
      this.showStatus('Recording voice note')
      this.startTimer()
    } catch (_error) {
      this.setErrorState('Microphone permission is required for voice notes.')
      this.stopTracks()
    }
  }

  stopRecording ({ submitAfterStop = false } = {}) {
    if (!this.recording()) return

    this._submitAfterStop = submitAfterStop
    this.mediaRecorder.stop()
    this.stopTimer()
    this.stopTracks()
  }

  supportedMimeType () {
    const choices = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
    return choices.find((type) => MediaRecorder.isTypeSupported(type))
  }

  buildAudioFile () {
    if (!this.hasAudioInputTarget || this.chunks.length === 0) return

    const blob = new Blob(this.chunks, { type: this.mediaRecorder?.mimeType || 'audio/webm' })
    const extension = blob.type.includes('mp4') ? 'm4a' : 'webm'
    const file = new File([blob], `voice-note-${Date.now()}.${extension}`, { type: blob.type })
    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.audioInputTarget.files = transfer.files
    this.audioInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    if (this._submitAfterStop) {
      this._submitAfterStop = false
      this.element.requestSubmit()
      return
    }

    this.setReadyState()
    this.showStatus('Voice note ready to send')
  }

  startTimer () {
    this.stopTimer()
    this._timer = window.setInterval(() => {
      if (!this.recording()) return

      const elapsed = Date.now() - this.recordingStartedAt
      const seconds = Math.floor(elapsed / 1000)
      this.updateTimer(seconds)

      if (elapsed >= this.maxDurationMs) {
        this.stopRecording({ submitAfterStop: false })
      }
    }, 300)
  }

  stopTimer () {
    if (!this._timer) return

    window.clearInterval(this._timer)
    this._timer = null
  }

  stopTracks () {
    if (!this.stream) return

    this.stream.getTracks().forEach((track) => track.stop())
    this.stream = null
  }

  showStatus (message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
  }

  updateTimer (seconds) {
    if (!this.hasTimerTarget) return

    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    this.timerTarget.textContent = `${mins}:${String(secs).padStart(2, '0')}`
  }

  setIdleState () {
    this.toggleComposer(false)
    this.toggleLiveDot(false)
    this.toggleReadyDot(false)
    this.setRecordButton(false)
    this.toggleTextInput(true)
    this.toggleDefaultSubmit(true)
    this.updateTimer(0)
  }

  setRecordingState () {
    this.toggleComposer(true)
    this.toggleLiveDot(true)
    this.toggleReadyDot(false)
    this.setRecordButton(true)
    this.toggleTextInput(false)
    this.toggleDefaultSubmit(false)
  }

  setReadyState () {
    this.toggleComposer(true)
    this.toggleLiveDot(false)
    this.toggleReadyDot(true)
    this.setRecordButton(false)
    this.toggleTextInput(false)
    this.toggleDefaultSubmit(false)
  }

  setErrorState (message) {
    this.toggleComposer(true)
    this.toggleLiveDot(false)
    this.toggleReadyDot(false)
    this.setRecordButton(false)
    this.toggleTextInput(true)
    this.toggleDefaultSubmit(true)
    this.updateTimer(0)
    this.showStatus(message)
  }

  toggleComposer (show) {
    if (!this.hasComposerTarget) return

    this.composerTarget.classList.toggle('hidden', !show)
  }

  toggleLiveDot (show) {
    if (!this.hasLiveDotTarget) return

    this.liveDotTarget.classList.toggle('hidden', !show)
  }

  toggleReadyDot (show) {
    if (!this.hasReadyDotTarget) return

    this.readyDotTarget.classList.toggle('hidden', !show)
  }

  setRecordButton (recording) {
    if (!this.hasRecordButtonTarget) return

    const classes = this.recordButtonTarget.classList
    classes.toggle('border-rose-300', recording)
    classes.toggle('bg-rose-50', recording)
    classes.toggle('text-rose-600', recording)
    classes.toggle('hover:border-rose-400', recording)
    classes.toggle('hover:bg-rose-100', recording)
    classes.toggle('hover:text-rose-700', recording)
    classes.toggle('border-slate-200', !recording)
    classes.toggle('bg-slate-50', !recording)
    classes.toggle('text-slate-500', !recording)
    classes.toggle('hover:border-sky-300', !recording)
    classes.toggle('hover:bg-sky-50', !recording)
    classes.toggle('hover:text-sky-700', !recording)
  }

  toggleTextInput (show) {
    if (!this.hasTextInputTarget) return

    this.textInputTarget.classList.toggle('hidden', !show)
  }

  toggleDefaultSubmit (show) {
    if (!this.hasDefaultSubmitTarget) return

    this.defaultSubmitTarget.classList.toggle('hidden', !show)
  }
}
